import { useState, useEffect } from 'react';
import adminService from '../../../services/adminService';

const CancellationRequestsSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCancellationRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getCancellationRequests();
      setRequests(
        response.requests ||
        response.cancellationRequests ||
        response.data ||
        []
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load cancellation requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCancellationRequests();
  }, []);

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-500/20 text-amber-600 dark:text-yellow-400 border-yellow-500/50',
      approved: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/50',
      cancelled: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/50',
      completed: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/50',
    };
    return statusStyles[status?.toLowerCase()] || statusStyles.pending;
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      coach: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/50',
      scout: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/50',
      player: 'bg-cyan-500/20 text-jp-accent-ink border-cyan-500/50',
    };
    return roleStyles[role] || 'bg-jp-section text-jp-muted border-jp-line';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="bg-jp-section rounded-xl border border-jp-line p-6">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4">Cancellation Requests</h2>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-jp-section rounded-xl border border-jp-line p-6">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4">Cancellation Requests</h2>
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-jp-section rounded-xl border border-jp-line p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest">Cancellation Requests</h2>
          {requests.length > 0 && (
            <span className="px-2 py-0.5 bg-cyan-500/20 text-jp-accent-ink border border-cyan-500/50 rounded-full text-xs font-medium">
              {requests.filter((r) => r.status?.toLowerCase() === 'pending').length} pending
            </span>
          )}
        </div>
        <button
          onClick={fetchCancellationRequests}
          className="px-3 py-1.5 text-xs bg-jp-section hover:bg-jp-line text-jp-ink rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-8 text-jp-muted">
          <svg className="w-12 h-12 mx-auto mb-3 text-jp-muted/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <p className="text-sm">No cancellation requests</p>
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-jp-section z-10">
              <tr className="text-center text-jp-muted border-b border-jp-line">
                <th className="pb-3 font-medium">User</th>
                <th className="pb-3 font-medium">Role</th>
                <th className="pb-3 font-medium">Email</th>
                <th className="pb-3 font-medium">Plan</th>
                <th className="pb-3 font-medium">Period Ends</th>
                <th className="pb-3 font-medium">Requested On</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Outseta UID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-jp-line">
              {requests.map((request) => {
                const user = request.user || request;
                return (
                  <tr key={request._id} className="text-jp-ink text-center">
                    <td className="py-3 font-medium">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border capitalize ${getRoleBadge(user.role)}`}>
                        {user.role || '-'}
                      </span>
                    </td>
                    <td className="py-3 text-jp-muted text-xs">
                      {user.email || '-'}
                    </td>
                    <td className="py-3 text-jp-muted text-xs">
                      {request.subscription?.plan || '-'}
                    </td>
                    <td className="py-3 text-jp-muted text-xs">
                      {formatDate(request.subscription?.currentPeriodEnd)}
                    </td>
                    <td className="py-3 text-jp-muted text-xs">
                      {formatDate(request.requestedAt || request.createdAt)}
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border capitalize ${getStatusBadge(request.status)}`}>
                        {request.status || 'Pending'}
                      </span>
                    </td>
                    <td className="py-3 text-jp-muted text-xs font-mono">
                      {request.outsetaSubscriptionUid || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CancellationRequestsSection;
